import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { convertToUrlParams } from '@/functions/convertSearchParams';
import { relativePath } from '@/functions/redirectToRelativePath';
import { useScrollIfHashed } from '@/hooks/useScrollControl';

export const useSearchQuery = (
  customerSetting: CustomerSetting,
  initialSearchParams: SearchParams
) => {
  const [sort, setSort] = useState<'date' | 'new'>(
    initialSearchParams.sort === 'new' ? 'new' : 'date'
  );
  const [keyword, setKeyword] = useState<string>(
    initialSearchParams.keyword ?? ''
  );
  const [dates, setDates] = useState<Dayjs[]>(
    initialSearchParams.dates?.split(',').map(dayjs) ?? []
  );
  const [categoryId, setCategoryId] = useState<string | null>(
    initialSearchParams.category ?? null
  );
  const [areaId, setAreaId] = useState<string | null>(
    initialSearchParams.area ?? null
  );

  const areaEnabled = customerSetting.areas.length > 0;
  const gridItemSm = areaEnabled ? 6 : 4;

  const makeSearchParams = (
    disableOptions?: (keyof SearchParams)[]
  ): SearchParams => {
    const searchParams: SearchParams = {
      keyword: keyword !== '' ? keyword : undefined,
      dates:
        dates.length > 0
          ? dates.map((date) => date.format('YYYY-MM-DD')).join(',')
          : undefined,
      category: categoryId !== null ? categoryId : undefined,
      area: areaId !== null ? areaId : undefined,
      sort: sort === 'new' ? 'new' : undefined
    };
    if (disableOptions) {
      disableOptions.forEach((key) => {
        delete searchParams[key];
      });
    }
    return searchParams;
  };

  const makeSortSearchUrl = (sort: string) => {
    const queryString = convertToUrlParams({
      ...initialSearchParams,
      sort: sort === 'new' ? 'new' : undefined
    }).toString();
    return relativePath(
      `/list/${queryString ? `?${queryString}` : ''}#events`,
      customerSetting.domain
    );
  };

  // 絞り込みボタンを押す際はデフォルトのソート順に戻す
  const makeSearchButtonUrl = () => {
    const queryString = convertToUrlParams(
      makeSearchParams(['sort'])
    ).toString();
    return relativePath(
      `/list/${queryString ? `?${queryString}` : ''}#events`,
      customerSetting.domain
    );
  };

  const ref = useScrollIfHashed('searchBox');

  return {
    sort,
    keyword,
    dates,
    categoryId,
    areaId,
    setSort,
    setKeyword,
    setDates,
    setCategoryId,
    setAreaId,
    areaEnabled,
    gridItemSm,
    makeSearchParams,
    makeSortSearchUrl,
    makeSearchButtonUrl,
    ref
  };
};
